import { MAX_BATCH_SIZE, MAX_BULK_SIZE, TBulkUpdateData } from '../types/general'

interface simpleSortCB<T> {
  (a: T, b: T): number
}

export const simpleSort = <T, K>(cb: (v: T) => K): simpleSortCB<T> => {
  return (a: T, b: T): number => {
    if (cb(a) < cb(b)) {
      return -1
    }
    if (cb(a) > cb(b)) {
      return 1
    }
    return 0
  }
}

export const parseInteger = (value: string, defaultValue = 0): number => {
  const rc = parseInt(value, 10)
  if (isNaN(rc)) {
    return defaultValue
  }
  return rc
}

export const nullifyEmptyStrings = (obj: Record<string, string | null | undefined>) => {
  return Object.entries(obj).reduce(
    (res, [key, value]) => ({
      ...res,
      [key]: value === undefined ? undefined : value?.trim() || null,
    }),
    {}
  )
}

// This Batch API function will resolve once all batches resolve
// The function will throw an error as soon as one of the batches rejects
export const requestBatchAPI = <T, K>(
  records: T[],
  requestAPI: (chunk: T[]) => Promise<K[]>,
  batchSize = MAX_BATCH_SIZE
): Promise<K[]> => {
  const promises: Promise<K[]>[] = []
  for (let i = 0; i < records.length; i += batchSize) {
    promises.push(requestAPI(records.slice(i, i + batchSize)))
  }
  return Promise.all(promises).then((res) => res.flat())
}

export const requestBulkAPI = <T, K>(
  records: TBulkUpdateData<T>,
  requestAPI: (chunk: TBulkUpdateData<T>) => Promise<K[]>,
  bulkSize = MAX_BULK_SIZE
): Promise<K[]> => {
  const promises: Promise<K[]>[] = []
  for (let i = 0; i < records.ids.length; i += bulkSize) {
    promises.push(
      requestAPI({
        ids: records.ids.slice(i, i + bulkSize),
        data: records.data,
      })
    )
  }
  return Promise.all(promises).then((res) => res.flat())
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isEmpty = (value: any) => {
  return (
    // null or undefined
    value == null ||
    // has length and it's zero
    // eslint-disable-next-line no-prototype-builtins
    (value.hasOwnProperty('length') && value.length === 0) ||
    // is an Object and has no keys
    (value.constructor === Object && Object.keys(value).length === 0)
  )
}

export const sleep = (timeout: number) => new Promise((resolve) => setTimeout(resolve, timeout))
